import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import toast from 'react-hot-toast';

import { useBranding } from "../../context/BrandingContext";

// Inline SVGs to compensate for Lucide-React brand icon deletions
const TwitterIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} fill="currentColor" viewBox="0 0 24 24" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedinIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} fill="currentColor" viewBox="0 0 24 24" className={className}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const GithubIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} fill="currentColor" viewBox="0 0 24 24" className={className}>
    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482C19.138 20.193 22 16.44 22 12.017 22 6.484 17.522 2 12 2z"/>
  </svg>
);

export default function SaasFooter() {
  const [email, setEmail] = useState("");
  const { branding } = useBranding();

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success("Subscribed to newsletter!");
    setEmail("");
  };

  return (
    <footer id="contact" className="bg-slate-950 text-slate-400 border-t border-slate-900 pt-20 pb-10 overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Top footer row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-slate-900">
          
          {/* Brand Info */}
          <div className="md:col-span-4 space-y-6">
            <Link to="/" className="flex items-center gap-3">
              {branding?.logo && (
                <div className="bg-white rounded-lg p-1.5 shadow-sm border border-slate-200 dark:border-slate-800">
                  <img src={branding.logo} alt="Platform Logo" className="h-12 object-contain" />
                </div>
              )}
              <span className="text-2xl font-black bg-gradient-to-r from-violet-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent tracking-tight">
                {branding?.instituteName || "Skillzy.pro"}
              </span>
            </Link>
            <p className="text-xs font-semibold leading-relaxed text-slate-500 max-w-sm">
              The premium, AI-powered Multi-Tenant LMS infrastructure built to help schools, universities, and coaching academies launch digital campuses in minutes.
            </p>
            {/* Social badges */}
            <div className="flex gap-4">
              <a href="#" className="w-8 h-8 rounded-full bg-slate-900 border border-slate-800 hover:border-violet-500/50 hover:text-white flex items-center justify-center transition-colors">
                <TwitterIcon size={14} />
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-slate-900 border border-slate-800 hover:border-violet-500/50 hover:text-white flex items-center justify-center transition-colors">
                <LinkedinIcon size={14} />
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-slate-900 border border-slate-800 hover:border-violet-500/50 hover:text-white flex items-center justify-center transition-colors">
                <GithubIcon size={14} />
              </a>
            </div>
          </div>

          {/* Site Sitemap Links */}
          <div className="md:col-span-4 grid grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="text-xs font-black uppercase tracking-widest text-slate-300">Product</h4>
              <ul className="space-y-2 text-xs font-semibold text-slate-500">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#solutions" className="hover:text-white transition-colors">Solutions</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="text-xs font-black uppercase tracking-widest text-slate-300">Legal</h4>
              <ul className="space-y-2 text-xs font-semibold text-slate-500">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Support</a></li>
              </ul>
            </div>
          </div>

          {/* Newsletter section */}
          <div className="md:col-span-4 space-y-4">
            <h4 className="text-xs font-black uppercase tracking-widest text-slate-300">Stay Updated</h4>
            <p className="text-xs text-slate-500 leading-relaxed font-semibold">
              Subscribe to receive security notes, platform updates, and product release logs.
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 outline-none focus:border-violet-500 text-xs font-semibold text-white"
                placeholder="Enter email..."
              />
              <button
                type="submit"
                className="px-4 bg-violet-600 hover:bg-violet-700 text-white rounded-xl flex items-center justify-center transition-colors shadow-lg"
              >
                <ArrowRight size={14} />
              </button>
            </form>
          </div>

        </div>

        {/* Bottom copyright details */}
        <div className="pt-8 flex flex-col sm:flex-row justify-between items-center text-[10px] text-slate-650 font-bold uppercase gap-4 select-none">
          <span>{branding?.footerText || `© ${new Date().getFullYear()} Skillzy.pro. All rights reserved.`}</span>
          <div className="flex items-center gap-1">
            <Sparkles size={10} className="text-violet-500" />
            <span>Built for modern education</span>
          </div>
        </div>

      </div>
    </footer>
  );
}
